import { inject, Injectable } from '@angular/core';
import type { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { catchError, throwError } from 'rxjs';

import {
  ConnectError,
  UnauthorizedError,
  NotFoundError,
  BadRequestError,
  ServerError,
  GatewayTimeoutError,
  ForbiddenError,
  ClientError,
  TooLargePayloadError,
} from '../errors';
import { CustomAuthErrorHeader } from '../api.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  readonly #router = inject(Router);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(catchError((error) => throwError(() => this.#handleError(error))));
  }

  #handleError(error: HttpErrorResponse): Error {
    const status = error.status;
    switch (true) {
      case status === 0:
        return new ConnectError();
      case status === 400:
        return new BadRequestError(error.error);
      case status === 401:
        return new UnauthorizedError(this.#router.routerState.snapshot.url);
      case status === 403:
        if (error.headers.get(CustomAuthErrorHeader.authError)) {
          return new UnauthorizedError(this.#router.routerState.snapshot.url);
        }
        return new ForbiddenError();
      case status === 404:
        return new NotFoundError();
      case status === 413:
        return new TooLargePayloadError();
      case 400 <= status && status < 500:
        return new ClientError();
      case status === 504:
        return new GatewayTimeoutError();
      case 500 <= status && status < 600:
        return new ServerError();
      default:
        return error;
    }
  }
}
